import BN from "bignumber.js";
import ApiRequest from "@/common/lib/apiRequest";
import StakeApiRequest from "@/common/lib/stakeApiRequest";
import ECOSYSTEMS from "@/common/config/ecosystems";

const mutationTypes = {
  SET_STAKE_STATS: "SET_STAKE_STATS",
  SET_VAULTS_STATS: "SET_VAULTS_STATS",
  SET_VAULTS_STRATEGY: "SET_VAULTS_STRATEGY",
  SET_APYS: "SET_APYS",
  SET_HISTORICAL_VOLUME: "SET_HISTORICAL_VOLUME",
  SET_VOTING_POWERS: "SET_VOTING_POWERS",
};

const state = {
  stakeStats: {
    fetching: false,
    result: null,
    error: null,
  },
  vaultsStats: {
    fetching: false,
    result: null,
    error: null,
  },
  apys: {
    vaults: {},
    stake: {},
  },
  vaultsStrategy: {
    fetching: false,
    result: null,
    error: null,
  },
  historicalVolume: {
    fetching: false,
    result: null,
    error: null,
  },
  votingPowers: {
    fetching: false,
    result: null,
    error: null,
  },
};

const actions = {
  async fetchApys(context) {
    await Promise.all([
      (async () => {
        const vaults = await fetchVaultsApys(context);
        context.commit(mutationTypes.SET_APYS, { vaults });
      })(),
      (async () => {
        const stake = await fetchStakeApys(context);
        context.commit(mutationTypes.SET_APYS, { stake });
      })(),
    ]);
  },
  async fetchStakeStats({ commit }) {
    commit(mutationTypes.SET_STAKE_STATS, { fetching: true });
    try {
      const result = await ApiRequest.get("/staking/total_aum");
      commit(mutationTypes.SET_STAKE_STATS, { fetching: false, result });
    } catch (error) {
      commit(mutationTypes.SET_STAKE_STATS, { fetching: false, error });
    }
  },
  async fetchVaultsStats({ commit }) {
    commit(mutationTypes.SET_VAULTS_STATS, { fetching: true });
    try {
      const result = await ApiRequest.get("/sc/vault/total_aum");
      commit(mutationTypes.SET_VAULTS_STATS, { fetching: false, result });
    } catch (error) {
      commit(mutationTypes.SET_VAULTS_STATS, { fetching: false, error });
    }
  },
  async fetchVaultsStrategy({ rootGetters, commit }) {
    commit(mutationTypes.SET_VAULTS_STRATEGY, { fetching: true });
    try {
      const vaults = rootGetters["projectConfig/vaultGroups"].filter(
        vault => vault.active
      );
      const data = await Promise.all(
        vaults.map(async vault =>
          Promise.all(
            vault.tokens.map(async coin => {
              const ret = await ApiRequest.get(
                `/sc/vault/strategy/${vault.name}/${coin.coin}`
              );
              const value = {};
              if (Array.isArray(ret)) {
                value.name = "Multi-Strategy";
                value.isMulti = true;
                value.strategies = ret;
                value.nameRepresent = ret[0]?.name || "TBD";
              } else {
                value.name = ret.name;
              }
              return {
                vault: vault.name,
                token: coin.coin,
                value,
              };
            })
          )
        )
      );
      const result = data.flat().reduce(
        (acc, cur) => ({
          ...acc,
          [cur.vault]: {
            ...(acc[cur.vault] || {}),
            [cur.token]: cur.value,
          },
        }),
        {}
      );
      commit(mutationTypes.SET_VAULTS_STRATEGY, { fetching: false, result });
    } catch (error) {
      commit(mutationTypes.SET_VAULTS_STRATEGY, { fetching: false, error });
    }
  },
  async fetchHistoricalVolume({ rootGetters, commit }, { days }) {
    commit(mutationTypes.SET_HISTORICAL_VOLUME, { fetching: true });
    try {
      const data = await ApiRequest.get("/swu_total_staked", {
        params: {
          days_before_today: days,
        },
      });
      const { uvaultV1, uvaultV2, uvaultV3, ...stakeData } = data;
      const vaultsData = { ...uvaultV2, ...uvaultV3 };
      const { all: vaults, details: vaultsDetails } = getVaultsHistoricalVolume(
        vaultsData,
        rootGetters
      );
      const { all: stake, details: stakeDetails } = getStakeHistoricalVolume(
        stakeData,
        rootGetters
      );
      const result = {
        rawData: data,
        vaults: timeObjectToArray(vaults),
        stake: timeObjectToArray(stake),
        all: timeObjectToArray(getCombinedHistoricalVolume(stake, vaults)),
        details: {
          stake: Object.keys(stakeDetails).reduce(
            (acc, cur) => ({
              ...acc,
              [cur]: timeObjectToArray(stakeDetails[cur]),
            }),
            {}
          ),
          vaults: Object.keys(vaultsDetails).reduce(
            (acc, cur) => ({
              ...acc,
              [cur]: timeObjectToArray(vaultsDetails[cur]),
            }),
            {}
          ),
        },
      };
      commit(mutationTypes.SET_HISTORICAL_VOLUME, { result, fetching: false });
      return result;
    } catch (error) {
      commit(mutationTypes.SET_HISTORICAL_VOLUME, { error, fetching: false });
    }
  },
  async fetchVotingPowers({ rootGetters, commit }) {
    const ethereumProjects =
      rootGetters["projectConfig/ethereumStakingProjects"];
    const tendermintProjects =
      rootGetters["projectConfig/tendermintStakingProjects"];
    const sui = rootGetters["projectConfig/suiStakingProjects"];
    const solana = rootGetters["projectConfig/solanaStakingProjects"];
    const tResult = await fetchTendermintVotingPowers(tendermintProjects);
    const eResult = await fetchOthersVotingPowers([...ethereumProjects, ...sui, ...solana]);
    commit(mutationTypes.SET_VOTING_POWERS, {
      result: { ...tResult, ...eResult },
    });
  },
};

//#region helpers
const fetchTendermintVotingPowers = async tendermintProjects => {
  const tendermintVotingPowers = await Promise.all(
    tendermintProjects.map(async pr => {
      const res = await getVotingPower(pr.config.network, pr.staking.chainId);
      return {
        [pr.projectName]: res,
      };
    })
  );
  const result = tendermintVotingPowers.reduce(
    (acc, cur) => ({ ...acc, ...cur }),
    {}
  );
  return result;
};

const getVotingPower = async (network, chainId) => {
  if (!chainId) {
    return {
      value: 0,
      percentage: 0,
    };
  }
  try {
    const result = await StakeApiRequest.get(
      `/total-staked/${network}/${chainId}`
    );
    return {
      value: result.swuStaked,
      percentage: new BN(result.swuStaked)
        .dividedBy(result.totalStaked)
        .multipliedBy(100)
        .toNumber(),
    };
  } catch (error) {
    console.log('GET_VOTING_POWER', error)
    return {
      value: 0,
      percentage: 0,
    };
  }
};

const _getVotingPower = async ({ projectName }) => {
  try {
    const _getTotalStakedUrl = () => {
      switch(projectName) {
        case 'kyberdao':
          return '/kyberdao/network/total_staked';
        case 'celer':
          return '/eth/celer/total_staked';
        case 'skale':
          return '/eth/skale/total_staked';
        case 'solana':
          return '/noneth/solana/total_staked';
        case 'sui':
          return '/noneth/sui/total_staked';
        default:
          console.error('PROJECT_NOT_FOUND', projectName);
          return null;
      }
    }
    const result = await ApiRequest.get(_getTotalStakedUrl(projectName));
    return {
      projectName: projectName,
      value: result.swuStaked,
      percentage: new BN(result.swuStaked)
        .dividedBy(result.totalStaked)
        .multipliedBy(100)
        .toNumber(),
    };
  } catch (error) {
    console.log('PROJECT', projectName);
    console.log(error)
    return {
      projectName,
      value: 0,
      percentage: 0,
    };
  }
}

const fetchOthersVotingPowers = async projects => {
  const votingPowers = await Promise.all(projects.map(_getVotingPower))
  const result = votingPowers.reduce(
    (acc, cur) => ({ ...acc, [cur.projectName]: cur }),
    {}
  );
  return result;
};

const getStakeHistoricalVolume = (stakeData, rootGetters) => {
  try {
    const getTokenPriceInUsd = rootGetters["appConfig/getTokenPriceInUsd"];
    const getProjectByName = rootGetters["projectConfig/getProjectByName"];
    const projects = Object.keys(stakeData);
    const all = {};
    const details = {};
    projects.forEach(projectName => {
      const project = getProjectByName(projectName);
      if (!project) return;
      const token = project.staking.token;
      const tokenPrice = getTokenPriceInUsd(token);
      const data = stakeData[projectName];
      details[projectName] = {};
      data.forEach(item => {
        const [year, month, day] = item.date.split("-");
        const createdAt = new Date(year, Number(month - 1), day).valueOf();
        all[createdAt] = new BN(all[createdAt] || 0)
          .plus(new BN(item.value).multipliedBy(tokenPrice))
          .toNumber();
        details[projectName][createdAt] = new BN(
          details[projectName][createdAt] || 0
        )
          .plus(new BN(item.value).multipliedBy(tokenPrice))
          .toNumber();
      });
    });
    return {
      details,
      all,
    };
  } catch (error) {
    console.log(error);
  }
};

const getVaultsHistoricalVolume = (vaultsData, rootGetters) => {
  const getTokenPriceInUsd = rootGetters["appConfig/getTokenPriceInUsd"];
  const result = {};
  const details = {};
  Object.keys(vaultsData).forEach(vault => {
    const token = vault.split("-")[1].toLocaleLowerCase();
    const tokenPrice = getTokenPriceInUsd(token);
    const data = vaultsData[vault];
    details[vault] = {};
    data.forEach(item => {
      const [year, month, day] = item.date.split("-");
      const createdAt = new Date(year, Number(month - 1), day).valueOf();
      result[createdAt] = new BN(result[createdAt] || 0)
        .plus(new BN(item.value).multipliedBy(tokenPrice))
        .toNumber();
      details[vault][createdAt] = new BN(details[vault][createdAt] || 0)
        .plus(new BN(item.value).multipliedBy(tokenPrice))
        .toNumber();
    });
  });
  return {
    all: result,
    details,
  };
};

const getCombinedHistoricalVolume = (...historicalData) => {
  const keys = [
    ...new Set(historicalData.map(item => Object.keys(item)).flat()),
  ];
  return keys.reduce(
    (acc, cur) => ({
      ...acc,
      [cur]: BN.sum(...historicalData.map(item => item[cur] || 0)).toNumber(),
    }),
    0
  );
};

const timeObjectToArray = (obj, mapFn) => {
  if (!mapFn) mapFn = (obj, key) => obj[key];
  const times = Object.keys(obj).sort((a, b) => a - b);
  return times.map(time => [Number(time), mapFn(obj, time)]);
};

const fetchVaultsApys = async context => {
  const vaults = context.rootGetters["projectConfig/vaultGroups"].filter(
    vault => vault.active
  );
  const data = await Promise.all(
    vaults.map(async vault =>
      Promise.all(
        vault.tokens.map(async item => {
          const ret = await ApiRequest.get(
            `/sc/vault/roi/${item.vault || vault.name}/${item.coin}`
          );
          return {
            vault: item.vault || vault.name,
            token: item.coin,
            value: ret,
          };
        })
      )
    )
  );
  const result = data.flat().reduce(
    (acc, cur) => ({
      ...acc,
      [cur.vault]: {
        ...(acc[cur.vault] || {}),
        [cur.token]: {
          ...cur.value,
          vault: cur.vault,
        },
      },
    }),
    {}
  );
  return result;
};

const fetchStakeApys = async context => {
  const [ethereum, tendermint, solana, sui] = await Promise.all([
    fetchEthereumsApys(),
    fetchTendermintsApy(context),
    fetchSolanaApy(context),
    fetchSuiApy(context),
  ]);
  return { ethereum, tendermint, solana, sui };
};

const getKyberApy = async () => {
  let value = 0;
  try {
    const data = await ApiRequest.get(`/kyberdao/network/staking_apr/latest`);
    value = data.value;
  } catch (error) {}
  return value;
};

const getEthereumProjectApy = async projectName => {
  let value = 0;
  try {
    const result = await ApiRequest.get(`/eth/${projectName}/apy`);
    value = result.apy;
  } catch (error) {}
  return value;
};

const fetchEthereumsApys = async () => {
  const [skale, celer] = await Promise.all([
    getEthereumProjectApy("skale"),
    getEthereumProjectApy("celer"),
  ]);

  const result = {
    skale: {
      apy: skale,
    },
    celer: {
      apy: celer,
    },
  };
  return result;
};

const fetchTendermintsApy = async ({ rootGetters }) => {
  const projectByNetworkMap = rootGetters["projectConfig/projectByNetworkMap"];
  const projects = ECOSYSTEMS[1].projects;
  const data = await Promise.all(
    projects.map(async project => {
      if (
        !projectByNetworkMap[project]?.status.active ||
        !projectByNetworkMap[project]?.staking?.chainId
      ) {
        return {
          network: project,
        };
      }
      try {
        if (projectByNetworkMap[project]?.variesApy) {
          return {
            network: project,
            apy: 0,
            note: 'Varies',
          }
        }
        let ret = {};
        if (project === "starname") {
          ret = {
            network: project,
            apy: 0.32,
            note: `Based off <a class="black--text" href="https://medium.com/iov-internet-of-values/what-apy-will-the-starname-iov-blockchain-have-when-inflation-is-0-5d623c6f0a27" target="_blank" rel="noopener noreferrer">estimates</a> from IOV team`,
          };
        } else {
          ret = await StakeApiRequest.get(
            `/yield/${project}/${projectByNetworkMap[project].staking.chainId}`
          );
        }
        return ret;
      } catch (error) {
        return {
          network: project,
        };
      }
    })
  );
  const result = data.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.network]: cur,
    }),
    {}
  );
  return result;
};

const fetchSolanaApy = async () => {
  let value = 0;
  try {
    const data = await ApiRequest.get(`/noneth/solana/apy`);
    value = data.apy;
  } catch (error) {}
  return { solana: { apy: value } };
};

const fetchSuiApy = async () => {
  let value = 0;
  try {
    const data = await ApiRequest.get(`/noneth/sui/apy`);
    value = data.apy;
  } catch (error) {}
  return { sui: { apy: value } };
};
//#endregion

const mutations = {
  [mutationTypes.SET_STAKE_STATS](state, data) {
    state.stakeStats = {
      ...state.stakeStats,
      ...data,
    };
  },
  [mutationTypes.SET_VAULTS_STATS](state, data) {
    state.vaultsStats = {
      ...state.vaultsStats,
      ...data,
    };
  },
  [mutationTypes.SET_APYS](state, data) {
    state.apys = {
      ...state.apys,
      ...data,
    };
  },
  [mutationTypes.SET_VAULTS_STRATEGY](state, data) {
    state.vaultsStrategy = {
      ...state.vaultsStrategy,
      ...data,
    };
  },
  [mutationTypes.SET_HISTORICAL_VOLUME](state, data) {
    state.historicalVolume = {
      ...state.historicalVolume,
      ...data,
    };
  },
  [mutationTypes.SET_VOTING_POWERS](state, data) {
    state.votingPowers = {
      ...state.votingPowers,
      ...data,
    };
  },
};

const getters = {
  vaultsStats: state => state.vaultsStats,
  stakeStats: state => state.stakeStats,
  stakeAUM: (state, getters, rootState, rootGetters) => {
    const getTokenPriceInUsd = rootGetters["appConfig/getTokenPriceInUsd"];
    if (!state.stakeStats.result) return 0;
    const value = state.stakeStats.result.reduce((acc, cur) => {
      return new BN(acc)
        .plus(
          new BN(Number(cur.tokens) || 0).multipliedBy(
            getTokenPriceInUsd(cur.tokenName)
          )
        )
        .toNumber();
    }, 0);
    return value;
  },
  vaultsTVL: (state, getters, rootState, rootGetters) => {
    const getTokenPriceInUsd = rootGetters["appConfig/getTokenPriceInUsd"];
    if (!state.vaultsStats.result) return 0;
    const value = state.vaultsStats.result.reduce((acc, cur) => {
      return new BN(acc)
        .plus(
          new BN(cur.expectedReturn).multipliedBy(
            getTokenPriceInUsd(cur.token.toLowerCase())
          )
        )
        .toNumber();
    }, 0);
    return value;
  },
  vaultsTVLByGroup: (state, getters, rootState, rootGetters) => {
    const getTokenPriceInUsd = rootGetters["appConfig/getTokenPriceInUsd"];
    if (!state.vaultsStats.result) return 0;
    const result = {};
    state.vaultsStats.result.forEach(vault => {
      const token = vault.token.toLowerCase();
      result[vault.vaultType] = new BN(result[vault.vaultType] || 0)
        .plus(
          new BN(vault.expectedReturn).multipliedBy(getTokenPriceInUsd(token))
        )
        .toNumber();
    });
    return result;
  },
  swuTotalAUM: (state, getters) => {
    return new BN(getters.stakeAUM).plus(getters.vaultsTVL).toNumber();
  },
  apys: state => state.apys,
  stakeApyMap: state => ({
    ...state.apys.stake.ethereum,
    ...state.apys.stake.tendermint,
    ...state.apys.stake.solana,
    ...state.apys.stake.sui,
  }),
  vaultsApyMap: state => ({
    ...state.apys.vaults.safeV2,
    ...state.apys.vaults.growthV2,
  }),
  vaultsStrategy: state => state.vaultsStrategy,
  historicalVolume: state => state.historicalVolume,
  votingPowers: state => state.votingPowers,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
